import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Server,
  MonitorCog,
  Network,
  CheckCircle2,
} from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const iconMap = { ShieldCheck, Server, MonitorCog, Network };

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useSeo({
    title: "Layanan IT Profesional",
    description:
      "Layanan teknologi informasi: pengembangan sistem informasi, pengadaan perangkat TI, konsultasi, dan infrastruktur jaringan untuk B2B Indonesia.",
    path: "/layanan",
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(asArray(r.data))).catch(() => setServices([]));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="services-page">
      <Header />

      {/* HEADER */}
      <section className="bg-[#0F172A] text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 kt-grid-bg opacity-10" />
        <div className="kt-container relative">
          <div className="kt-eyebrow text-[#FCA5A5]">Layanan Profesional</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
            Solusi TI menyeluruh — dari pengembangan sistem hingga pengadaan
            perangkat.
          </h1>
          <p className="mt-6 text-zinc-300 max-w-2xl">
            Setiap layanan kami dirancang dengan standar enterprise dan
            disesuaikan dengan kebutuhan unik instansi Anda. Lihat detail
            ruang lingkup di bawah.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-20 md:py-28">
        <div className="kt-container space-y-20">
          {services.map((s, idx) => {
            const Icon = iconMap[s.icon] || Server;
            const reversed = idx % 2 === 1;
            return (
              <div
                key={s.id}
                id={s.slug}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center scroll-mt-24"
                data-testid={`service-block-${s.slug}`}
              >
                <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
                  <div className="aspect-[16/10] overflow-hidden border border-[#E4E4E7]">
                    <img
                      src={s.image_url}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}>
                  <div className="flex items-center gap-3 text-xs font-mono-kt text-[#52525B] uppercase tracking-[0.2em]">
                    <span className="text-[#DC2626]">
                      Service / {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#18181B] text-white grid place-items-center">
                      <Icon size={22} />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                      {s.title}
                    </h2>
                  </div>
                  <div className="kt-divider-red mt-5" />
                  <p className="mt-5 text-[#52525B] leading-relaxed">
                    {s.description}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {(s.features || []).map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-3 text-sm text-[#18181B]"
                      >
                        <CheckCircle2
                          size={18}
                          className="text-[#DC2626] mt-0.5 shrink-0"
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to={`/layanan/${s.slug}`}
                      className="kt-btn-primary"
                      data-testid={`service-${s.slug}-detail-link`}
                    >
                      Lihat Detail <ArrowRight size={16} />
                    </Link>
                    <Link
                      to="/kontak"
                      className="kt-btn-outline"
                      data-testid={`service-${s.slug}-cta`}
                    >
                      Diskusikan dengan Tim
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
